/**
 * Centralized utilities for generating download endpoints
 * This file provides type-safe functions for all download endpoints
 */

import { DOWNLOAD_ENDPOINTS } from '@/config/constants';

// Types for different file entities
export interface FileWithVersion {
  id: number;
  last_version_id?: number;
  lastVersionId?: number; // Alternative naming for whitelist files
}

export interface VersionFile {
  id: number;
}

/**
 * Generate download endpoint for study files
 */
export const getStudyFileDownloadEndpoint = (file: FileWithVersion): string => {
  const versionId = file.last_version_id || file.lastVersionId || file.id;
  return DOWNLOAD_ENDPOINTS.studyFile(file.id, versionId);
};

/**
 * Generate download endpoint for specific study file version
 */
export const getStudyFileVersionDownloadEndpoint = (
  fileId: number,
  versionId: number
): string => {
  return DOWNLOAD_ENDPOINTS.studyFileVersion(fileId, versionId);
};

/**
 * Generate download endpoint for CSV files
 */
export const getCsvFileDownloadEndpoint = (file: FileWithVersion): string => {
  const versionId = file.last_version_id || file.lastVersionId || file.id;
  return DOWNLOAD_ENDPOINTS.csvFile(file.id, versionId);
};

/**
 * Generate download endpoint for specific CSV file version
 */
export const getCsvFileVersionDownloadEndpoint = (
  fileId: number,
  versionId: number
): string => {
  return DOWNLOAD_ENDPOINTS.csvFileVersion(fileId, versionId);
};

/**
 * Generate download endpoint for whitelists
 */
export const getWhitelistDownloadEndpoint = (file: FileWithVersion): string => {
  const versionId = file.last_version_id || file.lastVersionId || file.id;
  const endpoint = DOWNLOAD_ENDPOINTS.whitelist(file.id, versionId);

  return endpoint;
};

/**
 * Generate download endpoint for specific whitelist version
 */
export const getWhitelistVersionDownloadEndpoint = (
  fileId: number,
  versionId: number
): string => {
  return DOWNLOAD_ENDPOINTS.whitelistVersion(fileId, versionId);
};

/**
 * Generate download endpoint for whitelisted study files
 */
export const getWhitelistedStudyFileDownloadEndpoint = (
  file: FileWithVersion
): string => {
  const versionId = file.last_version_id || file.lastVersionId || file.id;
  return DOWNLOAD_ENDPOINTS.whitelistedStudyFile(file.id, versionId);
};

/**
 * Generate download endpoint for specific whitelisted study file version
 */
export const getWhitelistedStudyFileVersionDownloadEndpoint = (
  fileId: number,
  versionId: number
): string => {
  return DOWNLOAD_ENDPOINTS.whitelistedStudyFileVersion(fileId, versionId);
};

/**
 * Generic function to get download endpoint based on file type
 */
export type FileType =
  | 'studyFile'
  | 'csvFile'
  | 'whitelist'
  | 'whitelistedStudyFile';

export const getDownloadEndpoint = (
  fileType: FileType,
  file: FileWithVersion,
  versionId?: number
): string => {
  const effectiveVersionId =
    versionId || file.last_version_id || file.lastVersionId || file.id;

  switch (fileType) {
    case 'studyFile':
      return DOWNLOAD_ENDPOINTS.studyFile(file.id, effectiveVersionId);
    case 'csvFile':
      return DOWNLOAD_ENDPOINTS.csvFile(file.id, effectiveVersionId);
    case 'whitelist':
      return DOWNLOAD_ENDPOINTS.whitelist(file.id, effectiveVersionId);
    case 'whitelistedStudyFile':
      return DOWNLOAD_ENDPOINTS.whitelistedStudyFile(
        file.id,
        effectiveVersionId
      );
    default:
      throw new Error(`Unknown file type: ${fileType}`);
  }
};

/**
 * Generic function to get version download endpoint
 */
export const getVersionDownloadEndpoint = (
  fileType: FileType,
  fileId: number,
  versionId: number
): string => {
  switch (fileType) {
    case 'studyFile':
      return DOWNLOAD_ENDPOINTS.studyFileVersion(fileId, versionId);
    case 'csvFile':
      return DOWNLOAD_ENDPOINTS.csvFileVersion(fileId, versionId);
    case 'whitelist':
      return DOWNLOAD_ENDPOINTS.whitelistVersion(fileId, versionId);
    case 'whitelistedStudyFile':
      return DOWNLOAD_ENDPOINTS.whitelistedStudyFileVersion(fileId, versionId);
    default:
      throw new Error(`Unknown file type: ${fileType}`);
  }
};
